/**
 * The glyphs that operate the app rather than describe a plant: search, filters,
 * back, and the two the full-screen viewer needs.
 *
 * Split out of `plants/PlantIcons` when that file outgrew the 350-line contract.
 * The seam is what they are FOR: a magnifier is not a fact about a plant, it is a
 * control, and it is reached from screens that have nothing to do with the
 * catalogue. They sit beside `Icon` itself for the same reason `BrandIcons` does
 * not own them — they belong to no one feature.
 *
 * Drawn in the shared `Icon` frame: 1.5px strokes, round caps. Shape names and
 * props are the contract; paths are not.
 */

import Icon from './Icon';

/**
 * The Filters button. Three tracks with a handle each, at different positions —
 * a sliders glyph rather than a funnel, because what it opens is a set of
 * independent adjustments and not a single narrowing pipe.
 */
export function SlidersIcon(props) {
    return (
        <Icon {...props}>
            <path d="M4 7h4M12.5 7H20M4 17h7.5M16 17h4" />
            <circle cx="10.2" cy="7" r="2.3" />
            <circle cx="13.8" cy="17" r="2.3" />
        </Icon>
    );
}

export function SearchIcon(props) {
    return (
        <Icon {...props}>
            <circle cx="11" cy="11" r="6.5" />
            <path d="m16 16 4.5 4.5" />
        </Icon>
    );
}

export function BackIcon(props) {
    return (
        <Icon {...props}>
            <path d="m14.5 5-7 7 7 7" />
        </Icon>
    );
}

/**
 * The other way along the deck (SPEC-025 D-10).
 *
 * `BackIcon` mirrored, and drawn rather than transformed: a chevron is four
 * numbers, and a CSS flip on an icon is a second way of saying what a `d`
 * attribute already says perfectly well.
 */
export function ForwardIcon(props) {
    return (
        <Icon {...props}>
            <path d="m9.5 5 7 7-7 7" />
        </Icon>
    );
}

/**
 * The cue in the hero's corner: this picture opens (SPEC-006 D-2).
 *
 * Four viewfinder corners and nothing inside them. Still not a magnifier, and
 * the reason has changed rather than gone: SPEC-017 put a zoom behind this tap,
 * but what the tap ITSELF promises is the frame growing to the screen — the
 * magnifier belongs on the control that magnifies, one screen further in.
 */
export function ExpandIcon(props) {
    return (
        <Icon {...props}>
            <path d="M9.6 4.2H4.2v5.4M14.4 4.2h5.4v5.4M9.6 19.8H4.2v-5.4M14.4 19.8h5.4v-5.4" />
        </Icon>
    );
}

/** The chosen one of a set — the language on screen, in the switcher. */
export function CheckIcon(props) {
    return (
        <Icon {...props}>
            <path d="m5 12.5 4.5 4.5L19 7" />
        </Icon>
    );
}

/**
 * Going into the picture (SPEC-017 D-5). A magnifier with a plus in it — the
 * one glyph nobody has to be taught, and the pinch's visible half.
 */
export function ZoomInIcon(props) {
    return (
        <Icon {...props}>
            <circle cx="11" cy="11" r="6.5" />
            <path d="M11 8.2v5.6M8.2 11h5.6" />
            <path d="m16 16 4.5 4.5" />
        </Icon>
    );
}

/**
 * Back to the whole picture on the whole screen.
 *
 * `ExpandIcon`'s corners turned inward, with the arrows that say which way they
 * are travelling: the frame coming back to the picture rather than the picture
 * growing past it. Deliberately not a magnifier-with-a-minus — zooming out is
 * only ever as far as fit here, and a minus promises it goes further.
 */
export function FitScreenIcon(props) {
    return (
        <Icon {...props}>
            <path d="M4.2 4.2 9 9M9 4.6V9H4.6" />
            <path d="M19.8 4.2 15 9M15 4.6V9h4.4" />
            <path d="M4.2 19.8 9 15M9 19.4V15H4.6" />
            <path d="M19.8 19.8 15 15M15 19.4V15h4.4" />
        </Icon>
    );
}

/** Leaving the full-screen viewer. */
export function CloseIcon(props) {
    return (
        <Icon {...props}>
            <path d="m6 6 12 12M18 6 6 18" />
        </Icon>
    );
}

/**
 * Keeping the app (SPEC-016). A phone with an arrow coming down into it —
 * the app moving onto the device, which is what installing a PWA actually is.
 *
 * Deliberately not a floppy disc or a cloud: nothing is being downloaded and
 * nothing is being saved to a file, and both glyphs promise a file manager.
 */
/**
 * The account section's glyph: a head and shoulders, no ring — the drawer's
 * glyphs sit in their own circular wash already.
 */
export function PersonIcon(props) {
    return (
        <Icon {...props}>
            <circle cx="12" cy="8.2" r="3.4" />
            <path d="M5.5 19.5c1.5-3.5 3.9-5.2 6.5-5.2s5 1.7 6.5 5.2" />
        </Icon>
    );
}

export function InstallIcon(props) {
    return (
        <Icon {...props}>
            <rect x="5" y="2.75" width="14" height="18.5" rx="2.5" />
            <path d="M12 7.5v6.5m0 0 2.5-2.5M12 14l-2.5-2.5" />
            <path d="M10 18.25h4" />
        </Icon>
    );
}

/** Being told when something arrives (SPEC-016). */
export function BellIcon(props) {
    return (
        <Icon {...props}>
            <path d="M18 9.5a6 6 0 1 0-12 0c0 4-1.5 5.25-1.5 5.25h15S18 13.5 18 9.5Z" />
            <path d="M13.75 18.25a2 2 0 0 1-3.5 0" />
        </Icon>
    );
}


/**
 * The four ways a plant can travel (SPEC-019 D-3, PR-19.8), drawn as what ARRIVES
 * at the other end rather than as what the button does. All four "share"; the
 * choice being offered is between two pictures, a page and an address.
 */

/** A framed picture with a horizon and a sun: the scenic card. */
export function PictureIcon(props) {
    return (
        <Icon {...props}>
            <rect x="3.2" y="5" width="17.6" height="14" rx="2.4" />
            <circle cx="8.6" cy="10" r="1.6" />
            <path d="M3.6 16.4 8.4 12l3.4 3 3-2.4 4.6 3.8" />
        </Icon>
    );
}

/**
 * A wide picture with two lines of caption under it: a post in a feed.
 *
 * Deliberately NOT a Facebook logo. A brand mark inside a product's own icon set
 * is the one glyph that would date the moment Meta redraws it, it cannot be
 * drawn in the single stroke weight every other icon here uses, and the row's
 * label already names the destination in the reader's own language. What the
 * glyph has to say is the SHAPE — wide, with words beneath rather than beside —
 * because that is the actual difference from the row above it.
 *
 * The two caption strokes are ragged and flush with the frame's left edge. Set
 * centred, they stop reading as text and start reading as a monitor on a stand.
 */
export function PostIcon(props) {
    return (
        <Icon {...props}>
            <rect x="3" y="3.8" width="18" height="10.6" rx="2" />
            <path d="M3.6 17.9h11.6M3.6 20.7h6.8" />
        </Icon>
    );
}

/** A page with ruled lines: the one-pager, which is a document and looks like one. */
export function SheetIcon(props) {
    return (
        <Icon {...props}>
            <path d="M6 3.4h7.6L18 7.8v12.8H6Z" />
            <path d="M13.4 3.6v4.4h4.4" />
            <path d="M8.8 12.4h6.4M8.8 15.6h6.4M8.8 18h4" />
        </Icon>
    );
}

/** Two links of a chain: the address, and the only one of the three that unfurls. */
export function LinkIcon(props) {
    return (
        <Icon {...props}>
            <path d="M10.3 13.7a3.6 3.6 0 0 0 5.1 0l2.6-2.6a3.6 3.6 0 0 0-5.1-5.1l-1.3 1.3" />
            <path d="M13.7 10.3a3.6 3.6 0 0 0-5.1 0L6 12.9a3.6 3.6 0 0 0 5.1 5.1l1.3-1.3" />
        </Icon>
    );
}

/**
 * The drawer's status disclosure (SPEC-018 D-10). A circle with an i: what the
 * app can tell you about itself, as against the three settings above it, which
 * are things you can change.
 */
export function InfoIcon(props) {
    return (
        <Icon {...props}>
            <circle cx="12" cy="12" r="8.6" />
            <path d="M12 11v5.2" />
            <path d="M12 7.9v.1" />
        </Icon>
    );
}
